import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * useAutoplayIntroVideo — plays a full-bleed intro video on slide mount
 * (SlideDeck only mounts the active slide, so mount === visible).
 *
 * Sound model (User-Wunsch): the navigation gesture (click/key) that
 * opened this slide counts as user activation, so we start the video
 * UNMUTED right away — sound plays without any extra click. Only if the
 * browser blocks unmuted autoplay (e.g. direct page load onto this
 * slide, no prior gesture) we fall back to muted autoplay; the first
 * click then unmutes (handled in SlideDeck.onDeckClick).
 *
 * IMPORTANT: `muted` is intentionally NOT set as a JSX attribute on the
 * <video> element — React re-renders would re-apply it and silence the
 * video again. All mute state is managed imperatively via the ref.
 */
export default function useAutoplayIntroVideo(sectionId, opts = {}) {
  const { forceMuted = false } = opts;
  const videoRef = useRef(null);
  const stageRef = useRef(null);

  // useLayoutEffect fires synchronously WITHIN the navigation gesture's
  // task (React flushes layout effects before the click/keydown task
  // ends). Calling play() here means the browser still sees an active
  // user gesture → unmuted playback is permitted in Chrome AND Firefox.
  useLayoutEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Admin-Toggle 08/2026: introsAudio=false → immer stumm bleiben.
    if (forceMuted) {
      v.muted = true;
      v.setAttribute('muted', '');
      v.volume = 0;
    } else {
      v.muted = false;
      v.removeAttribute('muted');
      v.volume = 1.0;
    }
    v.autoplay = true;
    try { v.currentTime = 0; } catch (_) { /* noop */ }
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.catch((err) => {
        // ONLY a real autoplay-policy block (NotAllowedError) triggers the
        // muted fallback. AbortError (play interrupted by pause, e.g.
        // StrictMode remount or fast slide change) must NOT mute — that
        // was silencing the video in dev despite a valid user gesture.
        if (!err || err.name !== 'NotAllowedError') return;
        v.muted = true;
        v.setAttribute('muted', '');
        const p2 = v.play();
        if (p2 && typeof p2.then === 'function') p2.catch(() => {});
      });
    }
  }, [forceMuted]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    return () => {
      try {
        v.pause();
        v.muted = true;
        v.setAttribute('muted', '');
        v.currentTime = 0;
      } catch (_) { /* noop */ }
    };
  }, [sectionId]);

  return { videoRef, stageRef };
}
